import { AdminPagination as ZhPagination } from "@/views/components/AdminPagination";
import { normalizeOperationColumns } from "@/views/components/ProTable";
import { Alert, Button, Card, Input, Select, Typography } from "antd";
import { useEffect, useState } from "react";
import { Link } from "react-router";
import { DateRangePicker as ZhDateRangePicker, Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request from "@/api";
import { DEFAULT_VALUE, DATE_PICKER_FORMAT } from "@/config";
import { SORT } from "@/meta/common";
import { RECEIPT_TYPE } from "@/meta/purchase/receipt";
import { SIGNATURE_STATUS } from "@/meta/purchase/delivery";
import { dateFormat } from "@/utils";
import { GOODS_STATE } from "@zhihaoscm/meta";

export interface DeliveryListItem {
    purchaseGoods?: {
        id?: string | number;
        projectName?: string;
        type?: number | string;
        productTypeName?: string;
        ships?: Array<{ cnName?: string; name?: string }>;
        receiverEnterprise?: { name?: string; realName?: string };
        supplierEnterprise?: { name?: string; realName?: string };
        quantity?: number | string;
        applyDate?: string;
        state?: number | string;
        receiptState?: number | string;
    };
}

export type DeliveryListQuery = {
    page: number;
    size: number;
    param?: string;
    name?: string;
    type?: number;
    state?: number[];
    receiptState?: number[];
    beginTime?: string;
    endTime?: string;
    sortKey?: string;
    sortOrder?: string;
};

export type DeliveryListResult = { list?: DeliveryListItem[]; total?: number };

export interface DeliveryPageProps {
    loadDeliveries?: (query: DeliveryListQuery) => Promise<DeliveryListResult>;
}

/** 复用旧 Vue 提货列表的 `/admin/delivery/paging` 分页接口。 */
export async function loadDeliveries(query: DeliveryListQuery): Promise<DeliveryListResult> {
    const { data } = await request.service.get<DeliveryListResult>("/admin/delivery/paging", { params: query });
    return data;
}

const defaultQuery: DeliveryListQuery = { page: 1, size: 10 };

/** 读取旧 Vue 企业对象的展示名称，缺失时使用统一占位符。 */
function enterpriseLabel(enterprise?: { name?: string; realName?: string }): string {
    return enterprise?.name || enterprise?.realName || DEFAULT_VALUE;
}

/** 将旧 Vue 列表的提货申请日期格式化为日期展示。 */
function deliveryDate(value?: string): string {
    return value ? dateFormat(value, DATE_PICKER_FORMAT) : DEFAULT_VALUE;
}

/** 将筛选控件值转换为旧 Vue ProTable 提交的分页参数。 */
function buildQuery(values: {
    param: string;
    name: string;
    type?: number;
    state?: number[];
    receiptState?: number[];
    beginTime: string;
    endTime: string;
}): DeliveryListQuery {
    return {
        ...defaultQuery,
        ...(values.param.trim() ? { param: values.param.trim() } : {}),
        ...(values.name.trim() ? { name: values.name.trim() } : {}),
        ...(values.type == null ? {} : { type: Number(values.type) }),
        ...(values.state?.length ? { state: values.state.map(Number) } : {}),
        ...(values.receiptState?.length ? { receiptState: values.receiptState.map(Number) } : {}),
        ...(values.beginTime ? { beginTime: values.beginTime } : {}),
        ...(values.endTime ? { endTime: values.endTime } : {})
    };
}

/** 将 zhihao 表格排序转换为旧 Vue 提货接口的申请日期排序参数。 */
function buildSortQuery(
    query: DeliveryListQuery,
    sort: { prop?: string; order: "ascending" | "descending" | null }
): DeliveryListQuery {
    const nextQuery = { ...query, page: 1 };
    delete nextQuery.sortKey;
    delete nextQuery.sortOrder;
    if (sort.prop === "date" && sort.order) {
        nextQuery.sortKey = "apply_date";
        nextQuery.sortOrder = SORT.getIntroByCode(sort.order);
    }
    return nextQuery;
}

/** 采购管理-提货申请列表：保留旧 Vue 的筛选、分页、详情入口、空态和失败反馈。 */
export function DeliveryPage({ loadDeliveries: load = loadDeliveries }: DeliveryPageProps) {
    const [query, setQuery] = useState<DeliveryListQuery>(defaultQuery);
    const [param, setParam] = useState("");
    const [name, setName] = useState("");
    const [type, setType] = useState<number>();
    const [state, setState] = useState<number[]>([]);
    const [receiptState, setReceiptState] = useState<number[]>([]);
    const [dateRange, setDateRange] = useState<[string, string] | null>(null);
    const [rows, setRows] = useState<DeliveryListItem[]>([]);
    const [total, setTotal] = useState(0);
    const [error, setError] = useState("");

    // 用 zhihao-ui-react 列契约承接 Vue 提货列表的嵌套采购货物字段和状态枚举。
    const columns: TableColumnProps<DeliveryListItem>[] = [
        {
            key: "id",
            label: "编号",
            width: 145,
            prop: "purchaseGoods",
            render: ({ row }) => {
                const id = row.purchaseGoods?.id;
                return id ? <Link to={`/purchasingManage/delivery/detail/${id}`}>{String(id)}</Link> : DEFAULT_VALUE;
            }
        },
        {
            key: "projectName",
            label: "项目",
            minWidth: 176,
            prop: "purchaseGoods",
            render: ({ row }) => row.purchaseGoods?.projectName || DEFAULT_VALUE
        },
        {
            key: "type",
            label: "类型",
            minWidth: 120,
            prop: "purchaseGoods",
            render: ({ row }) =>
                row.purchaseGoods?.type == null
                    ? DEFAULT_VALUE
                    : RECEIPT_TYPE.getIntroByCode(Number(row.purchaseGoods.type)) || DEFAULT_VALUE
        },
        {
            key: "productTypeName",
            label: "品类",
            width: 148,
            prop: "purchaseGoods",
            render: ({ row }) => row.purchaseGoods?.productTypeName || DEFAULT_VALUE
        },
        {
            key: "ships",
            label: "船名",
            minWidth: 148,
            prop: "purchaseGoods",
            render: ({ row }) =>
                row.purchaseGoods?.ships?.length
                    ? row.purchaseGoods.ships.map(ship => ship.cnName || ship.name || "").join("、")
                    : DEFAULT_VALUE
        },
        {
            key: "receiverEnterprise",
            label: "提货单位",
            minWidth: 204,
            prop: "purchaseGoods",
            render: ({ row }) => enterpriseLabel(row.purchaseGoods?.receiverEnterprise)
        },
        {
            key: "supplierEnterprise",
            label: "供货单位",
            minWidth: 204,
            prop: "purchaseGoods",
            render: ({ row }) => enterpriseLabel(row.purchaseGoods?.supplierEnterprise)
        },
        {
            key: "quantity",
            label: "吨数",
            minWidth: 120,
            prop: "purchaseGoods",
            render: ({ row }) => String(row.purchaseGoods?.quantity ?? DEFAULT_VALUE)
        },
        {
            key: "date",
            label: "提货申请日期",
            width: 134,
            prop: "date",
            sortable: "custom",
            render: ({ row }) => deliveryDate(row.purchaseGoods?.applyDate)
        },
        {
            key: "state",
            label: "提货状态",
            width: 92,
            prop: "purchaseGoods",
            render: ({ row }) =>
                row.purchaseGoods?.state == null
                    ? DEFAULT_VALUE
                    : GOODS_STATE.getIntroByCode(Number(row.purchaseGoods.state)) || DEFAULT_VALUE
        },
        {
            key: "receiptState",
            label: "签收状态",
            width: 92,
            prop: "purchaseGoods",
            render: ({ row }) => {
                const receiptState = row.purchaseGoods?.receiptState;
                if (receiptState == null) return DEFAULT_VALUE;
                const code = Number(receiptState);
                return (
                    <span style={{ color: SIGNATURE_STATUS.SIGNED.match(code) ? "#00000066" : "#000000e0" }}>
                        {SIGNATURE_STATUS.getIntroByCode(code) || DEFAULT_VALUE}
                    </span>
                );
            }
        },
        {
            key: "operation",
            label: "操作",
            fixed: "right",
            width: 160,
            prop: "purchaseGoods",
            render: () => null
        }
    ];

    useEffect(() => {
        let active = true;
        void load(query)
            .then(result => {
                if (!active) return;
                setRows(result.list || []);
                setTotal(result.total || 0);
                setError("");
            })
            .catch(() => {
                if (!active) return;
                setRows([]);
                setTotal(0);
                setError("提货申请加载失败");
            });
        return () => {
            active = false;
        };
    }, [load, query]);

    const submitFilters = () =>
        setQuery(
            buildQuery({
                param,
                name,
                type,
                state,
                receiptState,
                beginTime: dateRange?.[0] || "",
                endTime: dateRange?.[1] || ""
            })
        );

    const resetFilters = () => {
        setParam("");
        setName("");
        setType(undefined);
        setState([]);
        setReceiptState([]);
        setDateRange(null);
        setQuery(defaultQuery);
    };

    // 保持 Vue ProTable 的自定义申请日期排序契约及排序后回到第一页。
    const handleSortChange = (sort: { prop?: string; order: "ascending" | "descending" | null }) => {
        setQuery(current => buildSortQuery(current, sort));
    };

    return (
        <section aria-label="delivery-page">
            <Card className="flex h-full flex-1 flex-col [&_.ant-card-body]:flex [&_.ant-card-body]:min-h-0 [&_.ant-card-body]:flex-1 [&_.ant-card-body]:flex-col">
                <ZhTable<DeliveryListItem>
                    data={rows}
                columns={normalizeOperationColumns(columns)}
                    emptyText="暂无提货申请"
                    pagination={false}
                    rowKey={row => String(row.purchaseGoods?.id || "delivery")}
                    onSortChange={handleSortChange}
                    isShowResetBtn={false}
                    isShowSearchBtn={false}
                    tableHeader={
                        <div className="admin-table-composed-header">
                            <Typography.Title className="mb-4 mt-0" level={4}>
                                提货申请
                            </Typography.Title>
                            {error ? <Alert className="mb-4" message={error} role="alert" type="error" /> : null}
                            <div className="mb-4 flex flex-wrap items-center gap-2">
                                <Input
                                    aria-label="提货编号或项目名称"
                                    placeholder="请输入提货编号或项目名称"
                                    value={param}
                                    onChange={event => setParam(event.target.value)}
                                />
                                <ZhDateRangePicker
                                    aria-label="提货申请日期"
                                    endPlaceholder="结束时间"
                                    format={DATE_PICKER_FORMAT}
                                    rangeSeparator="-"
                                    startPlaceholder="开始时间"
                                    type="daterange"
                                    value={dateRange}
                                    valueFormat={DATE_PICKER_FORMAT}
                                    onChange={(_value, formattedValue) =>
                                        setDateRange(formattedValue ? [formattedValue[0], formattedValue[1]] : null)
                                    }
                                />
                                <Input
                                    aria-label="提货单位或收货单位"
                                    placeholder="输入提货单位、收货单位搜索"
                                    value={name}
                                    onChange={event => setName(event.target.value)}
                                />
                                <Select
                                    aria-label="提货类型"
                                    allowClear
                                    options={RECEIPT_TYPE.options}
                                    placeholder="请选择类型"
                                    value={type}
                                    onChange={setType}
                                />
                                <Select
                                    aria-label="提货状态"
                                    allowClear
                                    mode="multiple"
                                    options={GOODS_STATE.options}
                                    placeholder="请选择提货状态"
                                    value={state}
                                    onChange={setState}
                                />
                                <Select
                                    aria-label="签收状态"
                                    allowClear
                                    mode="multiple"
                                    options={SIGNATURE_STATUS.options}
                                    placeholder="请选择签收状态"
                                    value={receiptState}
                                    onChange={setReceiptState}
                                />
                                <Button type="primary" onClick={submitFilters}>
                                    查询
                                </Button>
                                <Button onClick={resetFilters}>重置</Button>
                            </div>
                        </div>
                    }
                    append={
                        <div className="admin-table-composed-pagination">
                            <ZhPagination
                                current={query.page}
                                pageSize={query.size}
                                total={total}
                                // 对齐旧版 Element Plus 分页的 sizes 选项，切换条数后回到第一页。
                                pageSizeOptions={[10, 20, 30, 40, 50, 100]}
                                showSizeChanger
                                hideOnSinglePage
                                onChange={(page, pageSize) =>
                                    setQuery(current => ({
                                        ...current,
                                        page: pageSize !== current.size ? 1 : page,
                                        size: pageSize
                                    }))
                                }
                            />
                        </div>
                    }
                />
            </Card>
        </section>
    );
}

export default DeliveryPage;
